import React, { useId } from "react";
import { Info, X } from "react-feather";
import { Dropdown } from "../Dropdown";
import { SegmentedPicker } from "../SegmentedPicker";
import type {
  AlertConditionCardProps,
  AlertConditionMode,
} from "./AlertConditionCard.types";
import {
  AutoHelperText,
  Card,
  DropdownField,
  Field,
  FieldLabel,
  Header,
  MetricStatus,
  RemoveButton,
  ThresholdInput,
  ThresholdRow,
  Title,
} from "./AlertConditionCard.styles";

const CONDITION_MODE_OPTIONS = [
  { id: "threshold", label: "Threshold" },
  { id: "auto", label: "Auto" },
];

const DEFAULT_AUTO_HELPER =
  "Triggers on any meaningful change to this metric.";

export const AlertConditionCard: React.FC<AlertConditionCardProps> = ({
  title = "Condition",
  value,
  metricOptions,
  comparatorOptions,
  metricDropdown,
  comparatorDropdown,
  currentMetricText,
  autoHelperText = DEFAULT_AUTO_HELPER,
  removeDisabled = false,
  onChange,
  onRemove,
  className,
}) => {
  const thresholdInputId = useId();

  return (
    <Card className={className} aria-label={title}>
      <Header>
        <Title>{title}</Title>
        <RemoveButton
          icon={X}
          size="md"
          iconSize={30}
          disabled={removeDisabled}
          aria-label={`Remove ${title.toLowerCase()}`}
          onClick={onRemove}
        />
      </Header>

      <SegmentedPicker
        options={CONDITION_MODE_OPTIONS}
        value={value.mode}
        variant="form"
        ariaLabel={`${title} type`}
        onChange={(mode) =>
          onChange({ mode: mode as AlertConditionMode })
        }
      />

      <Field>
        <FieldLabel as="span">Metric</FieldLabel>
        <DropdownField>
          <Dropdown
            value={value.metric}
            options={metricOptions}
            open={metricDropdown.open}
            triggerRef={metricDropdown.triggerRef}
            menuRef={metricDropdown.menuRef}
            menuRect={metricDropdown.menuRect}
            size="md"
            compact
            searchable
            query={metricDropdown.query}
            onQueryChange={metricDropdown.onQueryChange}
            searchPlaceholder="Search metric"
            placeholder="Select metric"
            ariaLabel={`${title} metric`}
            onToggle={metricDropdown.onToggle}
            onSelect={(metric) => {
              onChange({ metric });
              metricDropdown.onClose();
            }}
          />
        </DropdownField>
        {currentMetricText ? (
          <MetricStatus role="status">
            <Info size={14} aria-hidden />
            {currentMetricText}
          </MetricStatus>
        ) : null}
      </Field>

      {value.mode === "threshold" ? (
        <ThresholdRow>
          <Field>
            <FieldLabel as="span">Comparator</FieldLabel>
            <DropdownField>
              <Dropdown
                value={value.comparator}
                options={comparatorOptions}
                open={comparatorDropdown.open}
                triggerRef={comparatorDropdown.triggerRef}
                menuRef={comparatorDropdown.menuRef}
                menuRect={comparatorDropdown.menuRect}
                size="md"
                compact
                searchable
                query={comparatorDropdown.query}
                onQueryChange={comparatorDropdown.onQueryChange}
                searchPlaceholder="Search comparator"
                placeholder="Select comparator"
                ariaLabel={`${title} comparator`}
                onToggle={comparatorDropdown.onToggle}
                onSelect={(comparator) => {
                  onChange({ comparator });
                  comparatorDropdown.onClose();
                }}
              />
            </DropdownField>
          </Field>
          <Field>
            <FieldLabel htmlFor={thresholdInputId}>Threshold</FieldLabel>
            <ThresholdInput
              id={thresholdInputId}
              inputMode="decimal"
              autoComplete="off"
              value={value.threshold}
              placeholder="Enter threshold"
              onChange={(event) =>
                onChange({ threshold: event.target.value })
              }
            />
          </Field>
        </ThresholdRow>
      ) : (
        <AutoHelperText>{autoHelperText}</AutoHelperText>
      )}
    </Card>
  );
};

export default AlertConditionCard;
